import { SquareArrowOutUpRight } from "lucide-react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  IgCopyTextTypography,
  IgDivider,
  IgIconButton,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { Drawer } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { removePrefix } from "@src/features/analytics/baseDashboard/utils";
import { dataReconciliationReasonDescriptionMapping } from "@src/features/analytics/utils";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";
import { theme } from "@src/theme";

const classes = {
  title: `OrderExampleDrawer-title`,
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: 1300,
  "& .MuiDrawer-paper": {
    width: "350px",
    borderRadius: "0px",
    padding: "20px",
    paddingLeft: "40px",
  },
  [`& .${classes.title}`]: {
    fontSize: "20px",
    fontWeight: 600,
  },
}));

export const OrderExampleDrawer = () => {
  const dispatch = useDispatch();
  const drawer = useSelector(getDrawer("orderExamples"));
  const organization = useGetOrg();

  const handleDrawerClose = () => dispatch(closeDrawer("orderExamples"));
  if (!drawer) {
    return null;
  }

  return (
    <StyledDrawer anchor={"right"} open onClose={handleDrawerClose}>
      <Grid container direction={"column"} rowSpacing={1}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid size={10}>
            <IgTypography fontWeight={600} sx={{ fontSize: "18px" }}>
              {removePrefix(drawer.data["exclusion_reason"])}
            </IgTypography>
          </Grid>
          <Grid size={2}>
            <IgIconButton
              iconSize={18}
              iconType={"close"}
              onClick={handleDrawerClose}
            />
          </Grid>
        </Grid>
        <Grid>
          <IgTypography variant={"h3"}>
            {
              dataReconciliationReasonDescriptionMapping[
                removePrefix(drawer.data["exclusion_reason"])
              ]
            }
          </IgTypography>
        </Grid>
        <Grid>
          <IgDivider spacing={"s"} />
        </Grid>
        <Grid>
          <IgTypography fontWeight={600} variant={"h3"}>
            Example Order IDs:
          </IgTypography>
        </Grid>
        <Grid container direction={"column"} columnSpacing={1.2}>
          {Object.entries(drawer.data["example_orders"])
            .slice(0, 10)
            .map(([key, value]: [string, any], index) => (
              <Grid
                key={index}
                container
                direction={"row"}
                alignItems={"center"}
                columnSpacing={1.2}
              >
                <Grid>
                  <IgCopyTextTypography
                    text={value[1]}
                    copiedTooltip={(text) => `Order Name ${text} Copied`}
                  />
                </Grid>
                <Grid>
                  <IgCopyTextTypography
                    text={`(${value[0]})`}
                    customCopyText={value[0]}
                    copiedTooltip={() => `Order ID ${value[0]} Copied`}
                  />
                </Grid>
                {organization?.data?.org?.shopId && (
                  <Grid size={1}>
                    <IgTooltip title={"Open order in Shopify"}>
                      <IgIconButton
                        sx={{ color: theme.igBackgroundColors.accentSolid }}
                        onClick={() => {
                          window.open(
                            `https://admin.shopify.com/store/${organization.data.org.shopId?.replace(
                              ".myshopify.com",
                              ""
                            )}/orders/${value[0]}`
                          );
                        }}
                      >
                        <SquareArrowOutUpRight size={12} />
                      </IgIconButton>
                    </IgTooltip>
                  </Grid>
                )}
              </Grid>
            ))}
        </Grid>
      </Grid>
    </StyledDrawer>
  );
};
